import { useEffect, useRef, useState, useCallback } from 'react';

import { API_BASE_URL } from '../utils/constants';

export interface SmsCodeData {
  code: string;
  number: string;
  serviceName: string;
  countryName: string;
  externalServiceId: string;
  receivedAt: string;
  source: 'webhook' | 'polling';
}

export interface TicketMessageData {
  _id: string;
  ticket: string;
  sender: any;
  senderType: 'user' | 'admin';
  content: string;
  hasAttachments: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TicketAttachmentData {
  _id: string;
  message: string;
  ticket: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  filePath: string;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface TicketNotification {
  type: 'new_message' | 'status_updated' | 'connected' | 'error' | 'message_deleted';
  ticketId: string;
  message?: TicketMessageData;
  attachments?: TicketAttachmentData[];
  status?: string;
  messageId?: string;
}

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

interface UseSseConnectionOptions {
  onCodeReceived?: (data: SmsCodeData) => void;
  onTicketNotification?: (notification: TicketNotification) => void;
  onConnectionChange?: (state: ConnectionState) => void;
  autoConnect?: boolean;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
  sseUrl?: string;
}

export function useSseConnection(options: UseSseConnectionOptions = {}) {
  const {
    onCodeReceived,
    onTicketNotification,
    onConnectionChange,
    autoConnect = true,
    reconnectDelay = 3000,
    maxReconnectAttempts = 5,
    sseUrl = `${API_BASE_URL}/temporary-numbers/sse`,
  } = options;

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [receivedCodes, setReceivedCodes] = useState<Map<string, SmsCodeData>>(new Map());
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  const isActiveConnectionRef = useRef(false);
  const currentSseUrlRef = useRef(sseUrl);

  const updateConnectionState = useCallback(
    (newState: ConnectionState) => {
      if (!isMountedRef.current) return;
      setConnectionState(newState);
      onConnectionChange?.(newState);
    },
    [onConnectionChange]
  );

  const handleCodeReceived = useCallback(
    (data: SmsCodeData) => {
      if (!isMountedRef.current) return;
      
      // Use phone number as key (it's unique per temporary number)
      const key = data.number;
      
      setReceivedCodes((prev) => {
        const newMap = new Map(prev);
        newMap.set(key, data);
        return newMap;
      });
      
      onCodeReceived?.(data);
    },
    [onCodeReceived]
  );

  const connect = useCallback((url?: string) => {
    const targetUrl = url || currentSseUrlRef.current;
    currentSseUrlRef.current = targetUrl;

    if (isActiveConnectionRef.current) {
      // If already connected, check if URL changed
      if (eventSourceRef.current?.url?.includes(targetUrl)) {
        return;
      }
    }

    // Cleanup previous connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    isActiveConnectionRef.current = true;
    updateConnectionState('connecting');

    const eventSource = new EventSource(targetUrl, { withCredentials: true });
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      if (!isMountedRef.current) return;
      updateConnectionState('connected');
      reconnectAttemptsRef.current = 0;
    };

    eventSource.onmessage = (event: MessageEvent) => {
      if (!isMountedRef.current) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected') {
          return;
        }
        
        // Handle SMS code notifications
        if (data.code && data.number) {
          handleCodeReceived(data as SmsCodeData);
        }
        
        // Handle ticket notifications
        if (data.type === 'new_message' || data.type === 'status_updated' || data.type === 'message_deleted') {
          onTicketNotification?.(data as TicketNotification);
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    eventSource.onerror = () => {
      if (!isMountedRef.current) return;
      // Avoid handling the same failure multiple times (browser can fire onerror repeatedly)
      if (eventSourceRef.current !== eventSource) return;
      isActiveConnectionRef.current = false;
      eventSource.close();
      eventSourceRef.current = null;
      updateConnectionState('error');

      // Only one reconnect scheduled at a time to prevent request spam
      if (reconnectTimeoutRef.current != null) return;
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        updateConnectionState('disconnected');
        return;
      }
      reconnectAttemptsRef.current += 1;
      const delay = reconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1);
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        if (isMountedRef.current) {
          connect();
        }
      }, delay);
    };
  }, [handleCodeReceived, maxReconnectAttempts, reconnectDelay, updateConnectionState, onTicketNotification]);

  const disconnect = useCallback(() => {
    isActiveConnectionRef.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    updateConnectionState('disconnected');
    reconnectAttemptsRef.current = 0;
  }, [updateConnectionState]);

  // Refs so effect doesn't depend on connect/disconnect — they change every render when
  // options callbacks (e.g. onCodeReceived) are inline, which would cause disconnect+connect loop.
  const connectRef = useRef(connect);
  const disconnectRef = useRef(disconnect);
  const optionsRef = useRef(options);
  connectRef.current = connect;
  disconnectRef.current = disconnect;
  optionsRef.current = options;

  useEffect(() => {
    isMountedRef.current = true;

    if (autoConnect) {
      connectRef.current(optionsRef.current.sseUrl);
    }

    return () => {
      isMountedRef.current = false;
      disconnectRef.current();
    };
  }, [autoConnect]);

  const getCodeForNumber = useCallback(
    (phoneNumber: string): SmsCodeData | undefined => {
      return receivedCodes.get(phoneNumber);
    },
    [receivedCodes]
  );

  return {
    connectionState,
    receivedCodes,
    connect,
    disconnect,
    getCodeForNumber,
    isConnected: connectionState === 'connected',
    isConnecting: connectionState === 'connecting',
  };
}

